import * as tslib_1 from "tslib";
import * as _ from 'lodash';
import { promisify } from './promisify';
var SCOPE = {
    APP: 'APP',
    COMPONENT: 'COMPONENT',
};
export var viewModes = {
    EDITOR: 'Editor',
    SITE: 'Site',
    PREVIEW: 'Preview',
};
var WixAdapter = /** @class */ (function () {
    function WixAdapter(Wix) {
        this.Wix = Wix;
        //
    }
    WixAdapter.prototype.getCompId = function () {
        return this.Wix.Utils.getCompId();
    };
    WixAdapter.prototype.getWidgetStyle = function () {
        return Promise.all([
            promisify(this.Wix.Styles.getSiteColors),
            promisify(this.Wix.Styles.getSiteTextPresets),
            promisify(this.Wix.Styles.getStyleParams),
        ]).then(function (_a) {
            var siteColors = _a[0], siteTextPresets = _a[1], styleParams = _a[2];
            return ({
                siteColors: siteColors,
                siteTextPresets: siteTextPresets,
                styleParams: styleParams,
            });
        });
    };
    WixAdapter.prototype.getStyleParams = function () {
        return promisify(this.Wix.Styles.getStyleParams);
    };
    WixAdapter.prototype.getStyleId = function () {
        return promisify(this.Wix.Styles.getStyleId);
    };
    WixAdapter.prototype.getPageId = function () {
        return promisify(this.Wix.getCurrentPageId);
    };
    WixAdapter.prototype.resizeComponent = function (options) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.Wix.resizeComponent(options, resolve, reject);
        });
    };
    WixAdapter.prototype.getWidth = function () {
        return this.Wix.Utils.getWidth();
    };
    WixAdapter.prototype.getHeight = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getBoundingRectAndOffsets(function (info) { return resolve(_.get(info, 'rect.height')); });
        });
    };
    WixAdapter.prototype.onPublicDataChanged = function (cb) {
        this.Wix.addEventListener(this.Wix.Events.PUBLIC_DATA_CHANGED, cb);
    };
    WixAdapter.prototype.onStyleParamChanged = function (cb) {
        this.Wix.addEventListener(this.Wix.Events.STYLE_PARAMS_CHANGE, cb);
    };
    WixAdapter.prototype.onViewModeChanged = function (cb) {
        this.Wix.addEventListener(this.Wix.Events.EDIT_MODE_CHANGE, function (eventData) {
            eventData.editMode = _.capitalize(eventData.editMode);
            cb(eventData);
        });
    };
    WixAdapter.prototype.getPublicDataByKey = function (item) {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.Data.Public.get(item.key, { scope: item.scope || _this.Wix.Data.SCOPE.COMPONENT }, function (data) { return resolve(data); }, function () { return resolve(); });
        });
    };
    WixAdapter.prototype.openModal = function (url, _a) {
        var _this = this;
        var width = _a.width, height = _a.height, theme = _a.theme;
        return new Promise(function (resolve) {
            _this.Wix.openModal(url, width, height, resolve, theme || _this.Wix.Theme.DEFAULT);
        });
    };
    WixAdapter.prototype.closeWindow = function (message) {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.closeWindow(message);
            resolve();
        });
    };
    WixAdapter.prototype.startLoading = function () {
        //NOOP on purpose. Done by loader on parent.
    };
    WixAdapter.prototype.loaded = function () {
        this.Wix.Performance.applicationLoaded();
    };
    /**
     * Transform [{key: "key1", scope: 'APP'}, {key: "key2", scope: 'COMPONENT'}] to
     * {'APP': {key1: 'value1'}, 'COMPONENT': {key2: 'value2'}}
     * values are the result of Wix.Data.Public.get(<Key>)
     * @param items - Array of key names of the publicData (same key that is used in Wix.Data.Public.Set(key)
     * @returns {Promise<object>}
     */
    WixAdapter.prototype.getPublicDataByKeys = function (items) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var reduceValues, filterByScope, getPublicDataArray, appData, componentData;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        reduceValues = function (array) { return array.reduce(function (o, item) { return _.assign(o, item); }, array.length > 0 ? {} : null); };
                        filterByScope = function (array, scope) { return _.filter(array, { scope: scope }); };
                        getPublicDataArray = function (array) { return Promise.all(array.map(function (item) { return _this.getPublicDataByKey(item); })); };
                        return [4 /*yield*/, getPublicDataArray(filterByScope(items, SCOPE.APP))];
                    case 1:
                        appData = _a.sent();
                        return [4 /*yield*/, getPublicDataArray(filterByScope(items, SCOPE.COMPONENT))];
                    case 2:
                        componentData = _a.sent();
                        return [2 /*return*/, {
                                APP: reduceValues(appData),
                                COMPONENT: reduceValues(componentData),
                            }];
                }
            });
        });
    };
    WixAdapter.prototype.getAllPublicData = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.Wix.Data.Public.getAll(resolve, reject);
        });
    };
    WixAdapter.prototype.trackEvent = function (eventName, params) {
        if (this.isLiveSiteMode()) {
            this.Wix.Analytics.trackEvent(eventName, params);
        }
    };
    WixAdapter.prototype.getExternalId = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getExternalId(resolve);
        });
    };
    WixAdapter.prototype.isLiveSiteMode = function () {
        return this.getViewMode() === viewModes.SITE;
    };
    WixAdapter.prototype.isEditorMode = function () {
        return this.getViewMode() === viewModes.EDITOR;
    };
    WixAdapter.prototype.isPreviewMode = function () {
        return this.getViewMode() === viewModes.PREVIEW;
    };
    WixAdapter.prototype.getViewMode = function () {
        return _.capitalize(this.Wix.Utils.getViewMode());
    };
    WixAdapter.prototype.getDeviceType = function () {
        return _.capitalize(this.Wix.Utils.getDeviceType());
    };
    WixAdapter.prototype.openPopup = function (url, _a, compId) {
        var _this = this;
        var width = _a.width, height = _a.height, position = _a.position, theme = _a.theme;
        return new Promise(function (resolve) {
            _this.Wix.openPopup(url, width, height, position, resolve, theme);
        });
    };
    WixAdapter.prototype.openPersistentPopup = function (url, _a, compId) {
        var width = _a.width, height = _a.height, position = _a.position, theme = _a.theme;
        return this.Wix.openPersistentPopup({
            url: url,
            width: width,
            height: height,
            position: position,
            theme: theme,
        });
    };
    WixAdapter.prototype.getPubSub = function () {
        return this.Wix.PubSub;
    };
    WixAdapter.prototype.getLocale = function () {
        return this.Wix.Utils.getLocale();
    };
    WixAdapter.prototype.onEveryPageNavigation = function (cb) {
        this.Wix.addEventListener(this.Wix.Events.PAGE_NAVIGATION, cb);
    };
    WixAdapter.prototype.onNavigationToComponentPage = function (cb) {
        this.Wix.addEventListener(this.Wix.Events.PAGE_NAVIGATION_IN, cb);
    };
    WixAdapter.prototype.getSectionUrl = function (sectionIdentifier) {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.Utils.getSectionUrl(sectionIdentifier, resolve);
        });
    };
    WixAdapter.prototype.getCurrentPageInfo = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getCurrentPageId(function (id) {
                resolve({ id: id });
            });
        });
    };
    WixAdapter.prototype.currentPage = function () {
        var res = { id: undefined };
        this.Wix.getCurrentPageId(function (pageId) { return (res.id = pageId); });
        return res;
    };
    WixAdapter.prototype.getSiteStructure = function () {
        var _this = this;
        var mapper = function (siteMap) {
            return siteMap
                .filter(function (page) { return !page.hidden; })
                .map(function (page) { return ({
                isHomePage: page.isHomePage,
                name: page.title,
                id: (page.pageId || '').replace('#', ''),
                url: page.url,
            }); });
        };
        return new Promise(function (resolve) {
            _this.Wix.getSiteMap(function (siteMap) {
                resolve({
                    lightboxes: [],
                    prefixes: [],
                    pages: mapper(siteMap),
                });
            });
        });
    };
    WixAdapter.prototype.navigateToSection = function (sectionInfo) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.Wix.Utils.navigateToSection(sectionInfo, reject);
        });
    };
    WixAdapter.prototype.navigateTo = function (linkData) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.Wix.navigateTo(linkData, reject);
        });
    };
    WixAdapter.prototype.getComponentInfo = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getComponentInfo(resolve);
        });
    };
    WixAdapter.prototype.getBiObj = function () {
        return {
            metaSiteId: this.Wix.Utils.getInstanceValue('metaSiteId'),
            biToken: this.Wix.Utils.getInstanceValue('biToken'),
            visitorId: this.Wix.Utils.getInstanceValue('aid'),
            ownerId: this.Wix.Utils.getInstanceValue('siteOwnerId'),
        };
    };
    WixAdapter.prototype.getDimensions = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getBoundingRectAndOffsets(function (result) {
                var isNumber = typeof result.rect.width === 'number';
                resolve({
                    width: isNumber ? Number(result.rect.width) / result.scale : result.rect.width,
                    height: isNumber ? Number(result.rect.height) / result.scale : result.rect.height,
                    top: result.rect.top,
                    left: result.rect.left,
                });
            });
        });
    };
    WixAdapter.prototype.getIsFullWidth = function () {
        return new Promise(function (resolve) {
            // typeof this.Wix.isFullWidth === 'function'
            //   ? this.Wix.isFullWidth(result => {
            //       resolve(result);
            //     })
            //   : resolve(false);
            resolve(false);
        });
    };
    WixAdapter.prototype.getApplicationFields = function (appDefinitionId) {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getApplicationFields(appDefinitionId, resolve);
        });
    };
    WixAdapter.prototype.isAppSectionInstalled = function (_a) {
        var _this = this;
        var sectionId = _a.sectionId, appDefinitionId = _a.appDefinitionId;
        return new Promise(function (resolve) {
            _this.Wix.isAppSectionInstalled(sectionId, { appDefinitionId: appDefinitionId }, function (isInstalled) {
                resolve(isInstalled);
            });
        });
    };
    WixAdapter.prototype.onInstanceChanged = function (callback) {
        this.Wix.addEventListener(this.Wix.Events.INSTANCE_CHANGED, callback);
    };
    WixAdapter.prototype.getSiteInfo = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.getSiteInfo(resolve);
        });
    };
    WixAdapter.prototype.setPageMetaData = function (options, compId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                this.Wix.setPageMetadata({ title: options.title.text, description: options.description });
                return [2 /*return*/];
            });
        });
    };
    WixAdapter.prototype.getUid = function () {
        return this.Wix.Utils.getUid();
    };
    WixAdapter.prototype.getCurrentMember = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.currentMember(resolve);
        });
    };
    WixAdapter.prototype.getSiteRevision = function () {
        return this.Wix.Utils.getSiteRevision ? this.Wix.Utils.getSiteRevision() : null;
    };
    WixAdapter.prototype.getPublicAPI = function (appDefinitionId) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            _this.Wix.getPublicAPI({ appDefinitionId: appDefinitionId }, resolve, reject);
        });
    };
    WixAdapter.prototype.accessibilityEnabled = function () {
        var _this = this;
        return new Promise(function (resolve) {
            _this.Wix.isVisualFocusEnabled(resolve);
        });
    };
    return WixAdapter;
}());
export { WixAdapter };
//# sourceMappingURL=wixAdapter.js.map